"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { navigation, NavigationItem } from "../../data/navigation";
import logo from "@/assets/images/logos/logo.svg";
import { LangueSwitcher } from "../common/LangueSwitcher";

interface HeaderProps {
  className?: string;
}

export function Header({ className = "" }: HeaderProps) {
  const t = useTranslations();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const bodyOverflowRef = useRef("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fonction pour désactiver le scroll mais sans changer la position visuelle
  const disableScroll = () => {
    bodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  };

  // Fonction pour réactiver le scroll
  const enableScroll = () => {
    document.body.style.overflow = bodyOverflowRef.current;
  };

  const toggleMenu = () => {
    if (!mobileMenuOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Variants simplifiés cohérents avec l'ADN Virtuosa
  const menuVariants = {
    closed: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
    open: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  const containerVariants = {
    closed: { opacity: 0 },
    open: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      y: 20,
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <motion.header
        role="banner"
        className={`fixed left-0 right-0 top-0 z-40 flex w-full items-center px-4 backdrop-blur-[10px] transition-all duration-300 ph:px-24 md:px-36 lg:px-48 xl:px-60 ${className} ${
          mobileMenuOpen
            ? "h-16 bg-primary/80 text-white"
            : isScrolled
              ? "h-16 bg-primary/80 text-white"
              : "h-16 bg-transparent text-white"
        }`}
      >
        <div className="flex w-full items-center justify-between py-4">
          {/* Logo */}
          <Link href="/" className="z-50">
            <div className="w-[100px]">
              <Image
                src={logo}
                alt="Virtuosa - Agence Web Premium Paris | Logo"
                className="h-auto w-full"
                width={100}
                height={100}
                priority={true}
              />
            </div>
          </Link>

          {/* Navigation sur desktop */}
          <div className="ml-[3rem] hidden flex-1 items-center justify-center md:flex">
            <div className="flex items-center gap-7">
              {navigation.map((item: NavigationItem) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="text-accent hover:text-secondary"
                >
                  {t(`navigation.${item.translationKey}`)}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden items-center space-x-4 md:flex">
            <LangueSwitcher />
            <PrimaryButton
              text={t("primaryButton.text")}
              navigateTo="https://calendly.com/agence-virtuosa/appel-decouverte"
              target="_blank"
            />
          </div>

          {/* Bouton menu mobile */}
          <button
            onClick={toggleMenu}
            className="z-50 md:hidden"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#121212"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="block h-0.5 w-8 bg-secondary transition-all duration-300" />
                <span className="block h-0.5 w-8 bg-secondary transition-all duration-300" />
                <span className="block h-0.5 w-8 bg-secondary transition-all duration-300" />
              </div>
            )}
          </button>
        </div>
      </motion.header>

      {/* Separator */}
      <div
        className={`fixed left-0 right-0 top-16 z-40 h-[1px] w-full px-4 transition-all duration-300 ph:px-24 md:px-36 lg:px-48 xl:px-60`}
      >
        <div className="h-full w-full bg-tertiary" />
      </div>

      {/* Menu mobile overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-30 bg-primary md:hidden"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            onTouchMove={(e) => e.preventDefault()}
            style={{ touchAction: "none" }}
          >
            <div className="flex h-full flex-col items-center justify-center overflow-hidden px-4">
              <motion.div
                variants={containerVariants}
                initial="closed"
                animate="open"
                className="flex w-full flex-col items-center gap-6 text-center"
              >
                {navigation.map((item: NavigationItem) => (
                  <motion.div key={item.name} variants={itemVariants}>
                    <Link
                      href={item.href}
                      className="block text-2xl font-light text-accent transition-colors duration-300"
                      onClick={(e) => {
                        // Permet la navigation vers la même page sans rechargement
                        if (window.location.pathname === item.href) {
                          e.preventDefault();
                        }
                        toggleMenu();
                      }}
                    >
                      {t(`navigation.${item.translationKey}`)}
                    </Link>
                  </motion.div>
                ))}

                {/* Bouton Contact dans le menu mobile */}
                <motion.div variants={itemVariants} className="mt-8">
                  <PrimaryButton
                    text={t("primaryButton.text")}
                    navigateTo="https://calendly.com/agence-virtuosa/appel-decouverte"
                    target="_blank"
                    onClick={toggleMenu}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <LangueSwitcher />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
